/**
 * Canonical `/api/v1/...` paths for the Maktab backend.
 *
 * Keep aligned with the deployed OpenAPI document:
 * `GET https://maktab-backend.onrender.com/api/v1/openapi.json`
 * Repo snapshot: `openapi/openapi.json` (refresh with `./maktab api sync`).
 */

const v1 = '/api/v1'

/** System health (`/api/v1/health...`) — paths match OpenAPI exactly (some omit trailing `/`). */
export const systemPaths = {
  health: `${v1}/health/`,
  healthCache: `${v1}/health/cache`,
  healthDb: `${v1}/health/db`,
} as const

/** Accounts & auth (`/api/v1/accounts/...`) */
export const accountsPaths = {
  login: `${v1}/accounts/login/`,
  registerParent: `${v1}/accounts/register/parent/`,
  verifyParent: `${v1}/accounts/register/parent/verify/`,
  /** "Email me a new link" for an unverified parent sign-up (same response for any email). */
  resendParentVerification: `${v1}/accounts/register/parent/resend-verification/`,
  /** JWT refresh — body `RefreshTokenBodySchema`: `{ "refresh_token": "<jwt>" }` */
  refreshToken: `${v1}/accounts/refresh-token/`,
  logout: `${v1}/accounts/logout/`,
  profile: `${v1}/accounts/profile/`,
  passwordChange: `${v1}/accounts/password/change/`,
  passwordResetRequest: `${v1}/accounts/password/reset/request/`,
  passwordResetConfirm: `${v1}/accounts/password/reset/confirm/`,
  adminUsers: `${v1}/accounts/admin/users/`,
  adminUser: (userId: number | string) => `${v1}/accounts/admin/users/${userId}/`,
  adminUserDisable: (userId: number | string) => `${v1}/accounts/admin/users/${userId}/disable/`,
  adminUserEnable: (userId: number | string) => `${v1}/accounts/admin/users/${userId}/enable/`,
  adminUserAssignSchool: (userId: number | string) =>
    `${v1}/accounts/admin/users/${userId}/assign-school/`,
  adminUserRemoveSchool: (userId: number | string, schoolId: string) =>
    `${v1}/accounts/admin/users/${userId}/remove-school/${schoolId}/`,
  adminUserChangeRole: (userId: number | string) =>
    `${v1}/accounts/admin/users/${userId}/change-role/`,
  adminUserAssignRole: (userId: number | string) =>
    `${v1}/accounts/admin/users/${userId}/assign-role/`,
  adminUserRemoveRole: (userId: number | string) =>
    `${v1}/accounts/admin/users/${userId}/remove-role/`,
  adminParentEmailChange: (parentId: number | string) =>
    `${v1}/accounts/admin/parents/${parentId}/email-change/`,
  adminParentEmailChangeResend: (parentId: number | string) =>
    `${v1}/accounts/admin/parents/${parentId}/email-change/resend/`,
  adminParentEmailChangeCancel: (parentId: number | string) =>
    `${v1}/accounts/admin/parents/${parentId}/email-change/cancel/`,
  emailChangeLookup: `${v1}/accounts/email-change/lookup/`,
  emailChangeConfirm: `${v1}/accounts/email-change/confirm/`,
  /** Public: an imported family's parent opens their invite link and sets a password. */
  parentInviteLookup: `${v1}/accounts/parent-invite/lookup/`,
  parentInviteAccept: `${v1}/accounts/parent-invite/accept/`,
  invitations: `${v1}/accounts/invitations/`,
  invitationResend: (inviteId: number | string) => `${v1}/accounts/invitations/${inviteId}/resend/`,
  invitationRevoke: (inviteId: number | string) => `${v1}/accounts/invitations/${inviteId}/revoke/`,
  /** Public (no auth): `?token=<uuid>` returns the invitation summary shown before Google sign-in. */
  invitationPreview: `${v1}/accounts/invitations/preview/`,
  loginHistory: `${v1}/accounts/login-history/`,
  oauthGoogleLogin: `${v1}/accounts/oauth/google/login/`,
} as const

/** Schools (`/api/v1/schools/...`) */
export const schoolsPaths = {
  list: `${v1}/schools/`,
  detail: (schoolId: string) => `${v1}/schools/${schoolId}/`,
  statistics: (schoolId: string) => `${v1}/schools/${schoolId}/statistics/`,
  gradeLevels: (schoolId: string) => `${v1}/schools/${schoolId}/grade-levels/`,
  gradeLevel: (schoolId: string, gradeLevelId: number | string) =>
    `${v1}/schools/${schoolId}/grade-levels/${gradeLevelId}/`,
  calendar: (schoolId: string) => `${v1}/schools/${schoolId}/calendar/`,
  calendarEvent: (schoolId: string, eventId: number | string) =>
    `${v1}/schools/${schoolId}/calendar/${eventId}/`,
  sections: (schoolId: string, gradeLevelId: number | string) =>
    `${v1}/schools/${schoolId}/grade-levels/${gradeLevelId}/sections/`,
  section: (schoolId: string, gradeLevelId: number | string, sectionId: number | string) =>
    `${v1}/schools/${schoolId}/grade-levels/${gradeLevelId}/sections/${sectionId}/`,
  /** Grade scales — list/create under school; detail under academics (MAK-128 / MAK-132) */
  gradeScales: (schoolId: string) => `${v1}/schools/${schoolId}/grade-scales/`,
  gradeScale: (gradeScaleId: string) => `${v1}/academics/grade-scales/${gradeScaleId}/`,
  gradeScaleRanges: (gradeScaleId: string) =>
    `${v1}/academics/grade-scales/${gradeScaleId}/ranges/`,
  /** Sysadmin org-wide school rollup — MAK-198 / dashboards-design §7.3 A3 */
  overview: `${v1}/schools/overview/`,
} as const

/** Academics (`/api/v1/academics/...`) */
export const academicsPaths = {
  schoolYears: `${v1}/academics/school-years/`,
  schoolYear: (schoolYearId: string) => `${v1}/academics/school-years/${schoolYearId}/`,
  schoolYearSetCurrent: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/set-current/`,
  classes: (schoolYearId: string) => `${v1}/academics/school-years/${schoolYearId}/classes/`,
  /** MAK-227 — transactional bulk create (optional `dry_run` query param) */
  classesBulk: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/classes/bulk/`,
  /** Atomic: one primary teacher (or null to clear) on many classes of a year */
  classesBulkAssignTeacher: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/classes/bulk-assign-teacher/`,
  classDetail: (classId: string) => `${v1}/academics/classes/${classId}/`,
  roster: (classId: string) => `${v1}/academics/classes/${classId}/roster/`,
  /** All-or-nothing: add listed students and/or the whole grade */
  rosterBulk: (classId: string) => `${v1}/academics/classes/${classId}/roster/bulk/`,
  rosterEntry: (classId: string, studentId: string) =>
    `${v1}/academics/classes/${classId}/roster/${studentId}/`,
  attendanceBulk: (classId: string) => `${v1}/academics/classes/${classId}/attendance/bulk/`,
  attendance: (classId: string) => `${v1}/academics/classes/${classId}/attendance/`,
  attendanceSummary: (classId: string) => `${v1}/academics/classes/${classId}/attendance/summary/`,
  markingPeriods: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/marking-periods/`,
  /** MAK-228 — date-policy action windows scoped to a school year */
  schoolYearActionWindows: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/action-windows/`,
  actionWindow: (actionWindowId: string) => `${v1}/academics/action-windows/${actionWindowId}/`,
  markingPeriod: (markingPeriodId: string) => `${v1}/academics/marking-periods/${markingPeriodId}/`,
  /** Marking period finalization — MAK-131 / MAK-135 */
  markingPeriodFinalize: (mpId: string) => `${v1}/academics/marking-periods/${mpId}/finalize/`,
  markingPeriodUnfinalize: (mpId: string) => `${v1}/academics/marking-periods/${mpId}/unfinalize/`,
  /** Aggregate grade status for a marking period — MAK-135 */
  markingPeriodGradeStatus: (mpId: string) =>
    `${v1}/academics/marking-periods/${mpId}/grade-status/`,
  schoolYearOverview: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/overview/`,
  /**
   * MAK-57 — GET returns defaults + per-grade rows; PUT .../defaults/; PUT .../grades/{id}/ per grade.
   * There is no GET .../fees/grades/ — use GET .../fees/ and read `grades` in the payload.
   */
  schoolYearFees: (schoolYearId: string) => `${v1}/academics/school-years/${schoolYearId}/fees/`,
  schoolYearFeeDefaults: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/fees/defaults/`,
  schoolYearGradeFee: (schoolYearId: string, gradeLevelId: number | string) =>
    `${v1}/academics/school-years/${schoolYearId}/fees/grades/${gradeLevelId}/`,
  rolloverPlan: (sourceSchoolYearId: string) =>
    `${v1}/academics/school-years/${sourceSchoolYearId}/rollover/plan/`,
  rollover: (rolloverId: string) => `${v1}/academics/rollovers/${rolloverId}/`,
  rolloverExecute: (rolloverId: string) => `${v1}/academics/rollovers/${rolloverId}/execute/`,
  reenrollmentRegistrations: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/reenrollment-registrations/`,
  /**
   * School schedule aggregate (GET), config, preview — MAK-164 / MAK-218 / MAK-222.
   * Day types and rotation are exposed here and via the routes below (no legacy bell/templates).
   */
  schoolSchedule: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/`,
  schoolScheduleConfig: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/config/`,
  schoolSchedulePreview: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/preview/`,
  schoolScheduleResolvedSessions: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/resolved-sessions/`,
  /** Create day type — POST */
  schoolScheduleDayTypes: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/day-types/`,
  schoolScheduleDayType: (schoolYearId: string, dayTypeId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/day-types/${dayTypeId}/`,
  /** Create period under a day type — POST; replace all periods atomically — PUT */
  schoolScheduleDayTypePeriods: (schoolYearId: string, dayTypeId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/day-types/${dayTypeId}/periods/`,
  /** Update/delete period by id — PATCH / DELETE (not nested under day-types/.../periods/{id}) */
  schoolScheduleDayTypePeriod: (schoolYearId: string, periodId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/day-type-periods/${periodId}/`,
  /** Replace rotation rows — PUT */
  schoolScheduleRotationWeekdayAssignments: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/rotation-weekday-assignments/`,
  /** Class offerings in the school schedule — POST; detail — PATCH / DELETE */
  schoolScheduleScheduledOfferings: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/scheduled-offerings/`,
  /** MAK-227 — bulk create scheduled offerings */
  schoolScheduleScheduledOfferingsBulk: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/scheduled-offerings/bulk/`,
  /** Pre-save clash check for proposed placements (nothing is saved) — POST */
  schoolScheduleScheduledOfferingsCheckConflicts: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/scheduled-offerings/check-conflicts/`,
  schoolScheduleScheduledOffering: (schoolYearId: string, offeringId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/scheduled-offerings/${offeringId}/`,
  schoolScheduleScheduledOfferingInstructors: (schoolYearId: string, offeringId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/scheduled-offerings/${offeringId}/instructors/`,
  schoolScheduleScheduledOfferingStudentOverrides: (schoolYearId: string, offeringId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/scheduled-offerings/${offeringId}/student-overrides/`,
  /** Drill-down: offerings in one period on a calendar date — GET */
  schoolSchedulePeriodOfferings: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/period-offerings/`,
  /** Teacher / room / student conflicts — GET */
  schoolScheduleConflicts: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/school-schedule/conflicts/`,
  /** School-year timetable (resolved offerings / slots for a date range) */
  timetable: (schoolYearId: string) => `${v1}/academics/school-years/${schoolYearId}/timetable/`,
  /** Gradebook: assignments and summary scoped to a class + marking period */
  classMarkingPeriodAssignments: (academicClassId: string, markingPeriodId: string) =>
    `${v1}/academics/classes/${academicClassId}/marking-periods/${markingPeriodId}/assignments/`,
  classMarkingPeriodGradebookSummary: (academicClassId: string, markingPeriodId: string) =>
    `${v1}/academics/classes/${academicClassId}/marking-periods/${markingPeriodId}/gradebook/summary/`,
  /** Teacher subject comments per class + marking period — MAK-130 / MAK-134 */
  classMarkingPeriodComments: (classId: string, mpId: string) =>
    `${v1}/academics/classes/${classId}/marking-periods/${mpId}/comments/`,
  classMarkingPeriodStudentComment: (classId: string, mpId: string, studentId: string) =>
    `${v1}/academics/classes/${classId}/marking-periods/${mpId}/comments/${studentId}/`,
  /** Assignment categories (weighted averages) per class + marking period — MAK-133 */
  classMarkingPeriodCategories: (classId: string, mpId: string) =>
    `${v1}/academics/classes/${classId}/marking-periods/${mpId}/categories/`,
  assignmentCategory: (categoryId: string) =>
    `${v1}/academics/assignment-categories/${categoryId}/`,
  assignment: (assignmentId: string) => `${v1}/academics/assignments/${assignmentId}/`,
  assignmentGrades: (assignmentId: string) => `${v1}/academics/assignments/${assignmentId}/grades/`,
  /** Per-student grade change audit trail — MAK-131 / MAK-135 */
  assignmentGradeHistory: (assignmentId: string, studentId: string) =>
    `${v1}/academics/assignments/${assignmentId}/grades/${studentId}/history/`,
  /** Class-wide grade change audit log — MAK-135 */
  gradeAuditLog: (classId: string) => `${v1}/academics/classes/${classId}/grade-change-log/`,
  /** Report cards */
  studentReportCards: (studentId: string) => `${v1}/academics/students/${studentId}/report-cards/`,
  studentReportCardsGenerate: (studentId: string) =>
    `${v1}/academics/students/${studentId}/report-cards/generate/`,
  /** Create (POST, 202) a background bulk report-card generation job. */
  reportCardBulkJobs: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/report-cards/bulk-jobs/`,
  /** Poll (GET) one bulk report-card generation job. */
  reportCardBulkJob: (schoolYearId: string, jobId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/report-cards/bulk-jobs/${jobId}/`,
  reportCardStatus: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/report-cards/status/`,
  bulkPublishReportCards: (schoolYearId: string) =>
    `${v1}/academics/school-years/${schoolYearId}/report-cards/bulk-publish/`,
  reportCard: (reportCardId: string) => `${v1}/academics/report-cards/${reportCardId}/`,
  reportCardPublish: (reportCardId: string) =>
    `${v1}/academics/report-cards/${reportCardId}/publish/`,
  reportCardCorrect: (reportCardId: string) =>
    `${v1}/academics/report-cards/${reportCardId}/correct/`,
  reportCardHistory: (reportCardId: string) =>
    `${v1}/academics/report-cards/${reportCardId}/history/`,
  /** Transcripts — student collection uses singular `transcript` */
  studentTranscript: (studentId: string) => `${v1}/academics/students/${studentId}/transcript/`,
  studentTranscriptGenerate: (studentId: string) =>
    `${v1}/academics/students/${studentId}/transcript/generate/`,
  transcript: (transcriptId: string) => `${v1}/academics/transcripts/${transcriptId}/`,
  transcriptPublish: (transcriptId: string) =>
    `${v1}/academics/transcripts/${transcriptId}/publish/`,
  /** Teacher-scoped dashboard aggregates — MAK-196 / dashboards-design §7.1 */
  teacherMeClasses: `${v1}/academics/me/classes/`,
  teacherMeTimetable: `${v1}/academics/me/timetable/`,
  teacherMeAttentionList: `${v1}/academics/me/attention-list/`,
  teacherMeAssignmentsUpcoming: `${v1}/academics/me/assignments/upcoming/`,
  teacherMeAttendanceWeekly: `${v1}/academics/me/attendance/weekly/`,
} as const

/** Registration (`/api/v1/registration/...`) */
export const registrationPaths = {
  health: `${v1}/registration/health/`,
  forms: `${v1}/registration/forms/`,
  formsOpen: `${v1}/registration/forms/open/`,
  formDetail: (formId: string) => `${v1}/registration/forms/${formId}/`,
  formPublish: (formId: string) => `${v1}/registration/forms/${formId}/publish/`,
  formFees: (formId: string) => `${v1}/registration/forms/${formId}/fees/`,
  formFeeStructures: (formId: string) => `${v1}/registration/forms/${formId}/fee-structures/`,
  formFeeStructure: (formId: string, feeStructureId: number | string) =>
    `${v1}/registration/forms/${formId}/fee-structures/${feeStructureId}/`,
  formDiscountPolicy: (formId: string) => `${v1}/registration/forms/${formId}/discount-policy/`,
  formGradeSettings: (formId: string) => `${v1}/registration/forms/${formId}/grade-settings/`,
  formGradeSetting: (formId: string, gradeSettingId: number | string) =>
    `${v1}/registration/forms/${formId}/grade-settings/${gradeSettingId}/`,
  formGradeAvailability: (formId: string) =>
    `${v1}/registration/forms/${formId}/grade-availability/`,
  formPriorRegistrations: (formId: string) =>
    `${v1}/registration/forms/${formId}/prior-registrations/`,
  formDrafts: (formId: string) => `${v1}/registration/forms/${formId}/drafts/`,
  formDraftsBatch: (formId: string) => `${v1}/registration/forms/${formId}/drafts/batch/`,
  formSubmit: (formId: string) => `${v1}/registration/forms/${formId}/submit/`,
  formInvites: (formId: string) => `${v1}/registration/forms/${formId}/invites/`,
  formInviteParents: (formId: string) => `${v1}/registration/forms/${formId}/invite-parents/`,
  inviteResend: (inviteId: string) => `${v1}/registration/invites/${inviteId}/resend/`,
  inviteRevoke: (inviteId: string) => `${v1}/registration/invites/${inviteId}/revoke/`,
  inviteLookup: `${v1}/registration/invites/lookup/`,
  inviteAccept: `${v1}/registration/invites/accept/`,
  myInvites: `${v1}/registration/my-invites/`,
  myRegistrations: `${v1}/registration/my-registrations/`,
  /** Family billing summary: status, schedule, pay options and next action per batch. */
  myBilling: `${v1}/registration/my-billing/`,
  /** App receipt for one settled payment (parent, school staff or system admin). */
  receipt: (paymentId: string) => `${v1}/registration/receipts/${paymentId}/`,
  /** Calendar-year family statement (`year`, optional `school_id`, staff `registration_id`). */
  statements: `${v1}/registration/statements/`,
  myChildren: `${v1}/registration/my-children/`,
  myChild: (identityId: string) => `${v1}/registration/my-children/${identityId}/`,
  /** MAK-197 — parent dashboard */
  myChildAttendance: (identityId: string) =>
    `${v1}/registration/my-children/${identityId}/attendance/`,
  myChildGradesLatest: (identityId: string) =>
    `${v1}/registration/my-children/${identityId}/grades/latest/`,
  myChildReportCards: (identityId: string) =>
    `${v1}/registration/my-children/${identityId}/report-cards/`,
  myChildReportCard: (identityId: string, reportCardId: string) =>
    `${v1}/registration/my-children/${identityId}/report-cards/${reportCardId}/`,
  myEvents: `${v1}/registration/my-events/`,
  myActionItems: `${v1}/registration/my-action-items/`,
  parentProfile: `${v1}/registration/parent-profile/`,
  draft: (registrationId: string) => `${v1}/registration/drafts/${registrationId}/`,
  /** Deprecated: only resumes a withdrawal committed before admin review existed. */
  registrationRequestCancellation: (registrationId: string) =>
    `${v1}/registration/registrations/${registrationId}/request-cancellation/`,
  /** Parent asks the school to withdraw children; a school admin decides. */
  withdrawalRequests: `${v1}/registration/withdrawal-requests/`,
  withdrawalRequestCancel: (requestId: string) =>
    `${v1}/registration/withdrawal-requests/${requestId}/cancel/`,
  adminWithdrawalRequests: `${v1}/registration/admin/withdrawal-requests/`,
  adminWithdrawalRequestApprove: (requestId: string) =>
    `${v1}/registration/admin/withdrawal-requests/${requestId}/approve/`,
  adminWithdrawalRequestDecline: (requestId: string) =>
    `${v1}/registration/admin/withdrawal-requests/${requestId}/decline/`,
  submission: (batchId: string) => `${v1}/registration/submissions/${batchId}/`,
  submissionInvoice: (batchId: string) => `${v1}/registration/submissions/${batchId}/invoice/`,
  paymentsPublishableKey: `${v1}/registration/payments/publishable-key/`,
  paymentsCheckout: `${v1}/registration/payments/checkout/`,
  paymentsCheckoutInstallment: `${v1}/registration/payments/checkout/installment/`,
  paymentsSplitPreview: (submissionBatchId: string) =>
    `${v1}/registration/payments/split-preview/${submissionBatchId}/`,
  paymentsCheckoutUpfront: `${v1}/registration/payments/checkout/upfront/`,
  paymentsCheckoutTuitionPlan: `${v1}/registration/payments/checkout/tuition-plan/`,
  paymentsCheckoutStatus: (sessionId: string) =>
    `${v1}/registration/payments/checkout/${sessionId}/status/`,
  paymentsPortal: `${v1}/registration/payments/portal/`,
  paymentsRegistration: (registrationId: string) =>
    `${v1}/registration/payments/registration/${registrationId}/`,
  /** MAK-166: batch-level plan detail (next payment date, tuition plan, payment history) */
  paymentsPlanDetail: (batchId: string) => `${v1}/registration/payments/plan/${batchId}/`,
  paymentsWebhook: `${v1}/registration/payments/webhook/`,
  adminPending: `${v1}/registration/admin/pending/`,
  adminStatistics: `${v1}/registration/admin/statistics/`,
  adminInvoicesExport: `${v1}/registration/admin/invoices/export/`,
  adminRegistrationApprove: (registrationId: string) =>
    `${v1}/registration/admin/registrations/${registrationId}/approve/`,
  adminRegistrationReject: (registrationId: string) =>
    `${v1}/registration/admin/registrations/${registrationId}/reject/`,
  adminRegistrationAcceptFromWaitlist: (registrationId: string) =>
    `${v1}/registration/admin/registrations/${registrationId}/accept-from-waitlist/`,
  adminRegistrationApplyDiscount: (registrationId: string) =>
    `${v1}/registration/admin/registrations/${registrationId}/apply-discount/`,
  adminRegistrationDiscountEligibility: (registrationId: string) =>
    `${v1}/registration/admin/registrations/${registrationId}/discount-eligibility/`,
  adminBillingAdjustment: (adjustmentId: string) =>
    `${v1}/registration/admin/billing-adjustments/${adjustmentId}/`,
  adminBillingAdjustmentRetry: (adjustmentId: string) =>
    `${v1}/registration/admin/billing-adjustments/${adjustmentId}/retry/`,
  adminBillingAdjustmentResolveProviderState: (adjustmentId: string) =>
    `${v1}/registration/admin/billing-adjustments/${adjustmentId}/resolve-provider-state/`,
  adminRegistrationDetail: (registrationId: string) =>
    `${v1}/registration/admin/registrations/${registrationId}/`,
  adminRegistrationArchive: (registrationId: string) =>
    `${v1}/registration/admin/registrations/${registrationId}/archive/`,
  adminRegistrationBulkArchive: `${v1}/registration/admin/registrations/bulk-archive/`,
  adminRegistrationDelete: (registrationId: string) =>
    `${v1}/registration/admin/registrations/${registrationId}/`,
  /** Returning students (mid-year re-enrollment campaigns) */
  reenrollmentSchoolOverview: (schoolId: string) =>
    `${v1}/registration/reenrollment/schools/${schoolId}/overview/`,
  reenrollmentCandidates: (formId: string) =>
    `${v1}/registration/reenrollment/forms/${formId}/candidates/`,
  reenrollmentReview: (formId: string) => `${v1}/registration/reenrollment/forms/${formId}/review/`,
  reenrollmentCreateCampaign: (formId: string) =>
    `${v1}/registration/reenrollment/forms/${formId}/campaign/`,
  reenrollmentCampaign: (campaignId: string) =>
    `${v1}/registration/reenrollment/campaigns/${campaignId}/`,
  reenrollmentCampaignStudents: (campaignId: string) =>
    `${v1}/registration/reenrollment/campaigns/${campaignId}/students/`,
  reenrollmentCampaignReminders: (campaignId: string) =>
    `${v1}/registration/reenrollment/campaigns/${campaignId}/reminders/`,
  reenrollmentCampaignExport: (campaignId: string) =>
    `${v1}/registration/reenrollment/campaigns/${campaignId}/export/`,
  reenrollmentInviteRespond: (inviteId: string) =>
    `${v1}/registration/reenrollment/invites/${inviteId}/respond/`,
  reenrollmentMyCampaigns: `${v1}/registration/reenrollment/my-campaigns/`,
  reenrollmentMyCampaign: (campaignId: string) =>
    `${v1}/registration/reenrollment/my-campaigns/${campaignId}/`,
  reenrollmentMyCampaignRespond: (campaignId: string) =>
    `${v1}/registration/reenrollment/my-campaigns/${campaignId}/respond/`,
} as const

/** Payments admin (`/api/v1/payments/admin/...`) */
export const paymentsPaths = {
  feed: `${v1}/payments/admin/feed/`,
  feedStats: `${v1}/payments/admin/feed/stats/`,
  feedExport: `${v1}/payments/admin/feed/export/`,
  /** Daily revenue series — MAK-198 / dashboards-design §7.3 A1 */
  feedRevenueSeries: `${v1}/payments/admin/feed/revenue-series/`,
  customers: `${v1}/payments/admin/customers/`,
  customer: (parentId: number | string) => `${v1}/payments/admin/customers/${parentId}/`,
  customerNotes: (parentId: number | string) => `${v1}/payments/admin/customers/${parentId}/notes/`,
  customerNote: (parentId: number | string, noteId: number | string) =>
    `${v1}/payments/admin/customers/${parentId}/notes/${noteId}/`,
  customerPortal: (parentId: number | string) =>
    `${v1}/payments/admin/customers/${parentId}/portal/`,
  followUpQueue: `${v1}/payments/admin/follow-up-queue/`,
  subscriptionConflicts: `${v1}/payments/admin/subscription-conflicts/`,
  resolveSubscriptionConflict: (conflictId: string) =>
    `${v1}/payments/admin/subscription-conflicts/${conflictId}/resolve/`,
  manual: `${v1}/payments/admin/manual/`,
  refund: `${v1}/payments/admin/refund/`,
  collectionState: (registrationId: string) =>
    `${v1}/payments/admin/registrations/${registrationId}/collection-state/`,
  balanceRelief: `${v1}/payments/admin/balance-relief/`,
} as const

/** Students (`/api/v1/students/...`) */
export const studentsPaths = {
  list: `${v1}/students/`,
  detail: (studentId: string) => `${v1}/students/${studentId}/`,
  enrollments: (studentId: string) => `${v1}/students/${studentId}/enrollments/`,
  withdraw: (studentId: string) => `${v1}/students/${studentId}/withdraw/`,
  safetyCard: (studentId: string) => `${v1}/students/${studentId}/safety-card/`,
  family: (studentId: string) => `${v1}/students/${studentId}/family/`,
  familyRelationship: (studentId: string, relationshipId: number | string) =>
    `${v1}/students/${studentId}/family/${relationshipId}/`,
  medical: (studentId: string) => `${v1}/students/${studentId}/medical/`,
  emergencyContacts: (studentId: string) => `${v1}/students/${studentId}/emergency-contacts/`,
  emergencyContact: (studentId: string, contactId: number | string) =>
    `${v1}/students/${studentId}/emergency-contacts/${contactId}/`,
  /** Identity-scoped documents (presigned upload-session + complete-upload + staff review). */
  identityDocuments: (identityId: string) => `${v1}/students/identities/${identityId}/documents/`,
  identityDocument: (identityId: string, documentId: number | string) =>
    `${v1}/students/identities/${identityId}/documents/${documentId}/`,
  identityDocumentUploadSession: (identityId: string) =>
    `${v1}/students/identities/${identityId}/documents/upload-session/`,
  identityDocumentCompleteUpload: (identityId: string, documentId: number | string) =>
    `${v1}/students/identities/${identityId}/documents/${documentId}/complete-upload/`,
  identityDocumentDownloadUrl: (identityId: string, documentId: number | string) =>
    `${v1}/students/identities/${identityId}/documents/${documentId}/download-url/`,
  identityDocumentVerify: (identityId: string, documentId: number | string) =>
    `${v1}/students/identities/${identityId}/documents/${documentId}/verify/`,
  identityDocumentReject: (identityId: string, documentId: number | string) =>
    `${v1}/students/identities/${identityId}/documents/${documentId}/reject/`,
  documentConstraints: `${v1}/students/documents/constraints/`,
  documentReviewQueue: `${v1}/students/documents/review-queue/`,
  notes: (studentId: string) => `${v1}/students/${studentId}/notes/`,
  note: (studentId: string, noteId: number | string) =>
    `${v1}/students/${studentId}/notes/${noteId}/`,
  transfer: (studentId: string) => `${v1}/students/${studentId}/transfer/`,
  transferHistory: (studentId: string) => `${v1}/students/${studentId}/transfer-history/`,
  bulkImport: `${v1}/students/bulk-import/`,
  /** CSV onboarding import (super admin only). */
  importTemplate: `${v1}/students/imports/template/`,
  imports: `${v1}/students/imports/`,
  importPreview: `${v1}/students/imports/preview/`,
  importUndo: (importId: string) => `${v1}/students/imports/${importId}/undo/`,
  /** Imported families' invites (super admin, system and school admins). */
  familyInvites: `${v1}/students/family-invites/`,
  familyInvitesSend: `${v1}/students/family-invites/send/`,
  familyInvitesRevoke: `${v1}/students/family-invites/revoke/`,
  familyInviteEmail: `${v1}/students/family-invites/invite-email/`,
  bulkGradePromotion: `${v1}/students/bulk-grade-promotion/`,
} as const

/**
 * FERPA log review and rights workflows for staff. `platform` routes need the super admin;
 * `school` routes accept school admins (scoped server-side) and the super admin.
 */
export const ferpaAdminPaths = {
  platform: {
    accessEvents: `${v1}/ferpa/access-events/`,
    accessEvent: (eventId: string) => `${v1}/ferpa/access-events/${eventId}/`,
    studentTimeline: (identityId: string) => `${v1}/ferpa/students/${identityId}/timeline/`,
    actorActivity: (userId: number | string) => `${v1}/ferpa/actors/${userId}/activity/`,
    disclosures: `${v1}/ferpa/disclosures/`,
    consents: `${v1}/ferpa/consents/`,
    requests: `${v1}/ferpa/requests/`,
    exports: `${v1}/ferpa/exports/`,
    export: (jobId: string) => `${v1}/ferpa/exports/${jobId}/`,
    insights: `${v1}/ferpa/insights/`,
  },
  school: {
    accessEvents: `${v1}/ferpa/school/access-events/`,
    accessEvent: (eventId: string) => `${v1}/ferpa/school/access-events/${eventId}/`,
    studentTimeline: (identityId: string) => `${v1}/ferpa/school/students/${identityId}/timeline/`,
    disclosures: `${v1}/ferpa/school/disclosures/`,
    disclosure: (disclosureId: string) => `${v1}/ferpa/school/disclosures/${disclosureId}/`,
    consents: `${v1}/ferpa/school/consents/`,
    requests: `${v1}/ferpa/school/requests/`,
    request: (requestId: string) => `${v1}/ferpa/school/requests/${requestId}/`,
    requestTransition: (requestId: string) =>
      `${v1}/ferpa/school/requests/${requestId}/transition/`,
    requestPackage: (requestId: string) => `${v1}/ferpa/school/requests/${requestId}/package/`,
    restrictions: `${v1}/ferpa/school/restrictions/`,
    endRestriction: (restrictionId: string) =>
      `${v1}/ferpa/school/restrictions/${restrictionId}/end/`,
    directoryPolicy: `${v1}/ferpa/school/directory-policy/`,
    directoryOptOuts: `${v1}/ferpa/school/directory-opt-outs/`,
    notices: `${v1}/ferpa/school/notices/`,
    publishNotice: (noticeId: string) => `${v1}/ferpa/school/notices/${noticeId}/publish/`,
  },
} as const

/** FERPA parent rights (`/ferpa/my/...`): a parent's own children only. */
export const ferpaParentPaths = {
  disclosures: (identityId: string) => `${v1}/ferpa/my/children/${identityId}/disclosures/`,
  consents: (identityId: string) => `${v1}/ferpa/my/children/${identityId}/consents/`,
  revokeConsent: (consentId: string) => `${v1}/ferpa/my/consents/${consentId}/revoke/`,
  requests: (identityId: string) => `${v1}/ferpa/my/children/${identityId}/requests/`,
  request: (requestId: string) => `${v1}/ferpa/my/requests/${requestId}/`,
  requestHearing: (requestId: string) => `${v1}/ferpa/my/requests/${requestId}/hearing/`,
  withdrawRequest: (requestId: string) => `${v1}/ferpa/my/requests/${requestId}/withdraw/`,
  requestStatements: (requestId: string) => `${v1}/ferpa/my/requests/${requestId}/statements/`,
  requestPackage: (requestId: string) => `${v1}/ferpa/my/requests/${requestId}/package/`,
  directoryOptOuts: (identityId: string) =>
    `${v1}/ferpa/my/children/${identityId}/directory-opt-outs/`,
  notices: `${v1}/ferpa/my/notices/`,
  acknowledgeNotice: (noticeId: string) => `${v1}/ferpa/my/notices/${noticeId}/acknowledge/`,
} as const
